/**
 * Space Rangers–style item catalog + procedural loot generation.
 * Icons are photoreal module photos, resolved at render time.
 */

export const RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
export const RARITY_MULT = { common: 1, uncommon: 1.25, rare: 1.55, epic: 2.0, legendary: 2.8 };
export const RARITY_COLOR = {
    common: '#8899aa', uncommon: '#44cc88', rare: '#44aaff',
    epic: '#cc66ff', legendary: '#ffaa22',
};

const SLOT_SHAPES = {
    weapon: 'weapon', weapon2: 'weapon2', shield: 'shield', engine: 'engine',
    reactor: 'reactor', hull: 'hull', sensor: 'sensor', cargo: 'cargo',
    special: 'special', wing: 'wing',
};

const PREFIXES = [
    'Void', 'Solar', 'Nebula', 'Quantum', 'Plasma', 'Ion', 'Nova', 'Astro',
    'Cryo', 'Photon', 'Grav', 'Hyper', 'Pulse', 'Arc', 'Stellar', 'Cosmic',
    'Lunar', 'Warp', 'Titan', 'Echo', 'Drift', 'Flux', 'Rift', 'Aether',
];

const SUFFIXES = {
    weapon:  ['Blaster', 'Cannon', 'Lance', 'Repeater', 'Rail', 'Scattergun', 'Beam'],
    weapon2: ['Turret', 'Pods', 'Array', 'Battery'],
    shield:  ['Barrier', 'Aegis', 'Ward', 'Deflector', 'Bulwark'],
    engine:  ['Thruster', 'Drive', 'Booster', 'Jet', 'Afterburner'],
    reactor: ['Core', 'Cell', 'Furnace', 'Dynamo', 'Heart'],
    hull:    ['Plating', 'Frame', 'Shell', 'Carapace', 'Armor'],
    sensor:  ['Scanner', 'Array', 'Scope', 'Radar', 'Lens'],
    cargo:   ['Hold', 'Bay', 'Container', 'Vault'],
    special: ['Module', 'Device', 'Matrix', 'Relic', 'Artifact'],
    wing:    ['Fins', 'Wings', 'Sails', 'Vanes'],
};

const TRADE_SHAPES = ['crystal', 'fuel', 'scrap', 'chip', 'drone', 'armor'];
const TRADE_NAMES = {
    crystal: ['Prism Dust', 'Void Crystal', 'Star Quartz', 'Ion Shard', 'Aether Gem'],
    fuel:    ['Hyperfuel', 'Plasma Canister', 'Deuterium', 'Warp Juice'],
    scrap:   ['Hull Scrap', 'Alloy Plates', 'Salvage Bundle', 'Wreck Parts'],
    chip:    ['Nav Chip', 'AI Core', 'Bioscript', 'Tactical ROM'],
    drone:   ['Scout Drone', 'Repair Bot', 'Sentry Pod'],
    armor:   ['Ablative Kit', 'Reactive Pad', 'Nano Mesh'],
};

const COLORS = [
    '#00d9ff', '#ff6644', '#66ffaa', '#ffcc33', '#cc66ff',
    '#ff44aa', '#44aaff', '#aaff44', '#ff8844', '#88eeff',
    '#ffee88', '#aa88ff', '#ff6688', '#66ffcc',
];

let _uid = 1;
function uid() { return `it_${Date.now().toString(36)}_${_uid++}`; }

function pick(arr, rng = Math.random) {
    return arr[Math.floor(rng() * arr.length)];
}

function rarityRoll(luck = 0) {
    const r = Math.random() + luck * 0.05;
    if (r > 0.97) return 'legendary';
    if (r > 0.90) return 'epic';
    if (r > 0.72) return 'rare';
    if (r > 0.45) return 'uncommon';
    return 'common';
}

/**
 * Create a ship module for a given slot.
 */
export function generateModule(slot = null, rarity = null) {
    const slots = Object.keys(SLOT_SHAPES);
    const s = slot && SLOT_SHAPES[slot] ? slot : pick(slots);
    const rar = rarity || rarityRoll();
    const mult = RARITY_MULT[rar];
    const color = pick(COLORS);
    const seed = Math.floor(Math.random() * 10000);
    const name = `${pick(PREFIXES)} ${pick(SUFFIXES[s] || ['Module'])}`;

    const stats = baseStatsForSlot(s, mult);
    const raw = Object.values(stats).reduce((a, b) => a + Math.abs(Number(b) || 0), 0);
    const price = Math.max(5, Math.round((20 + raw * 3) * mult));

    const item = {
        id: uid(),
        kind: 'module',
        slot: s,
        name,
        rarity: rar,
        color,
        seed,
        shape: SLOT_SHAPES[s],
        stats,
        price,
        desc: describeModule(s, stats),
        // Durability — 0 = broken (no stat bonus until repaired)
        durability: 100,
        maxDurability: 100,
        broken: false,
        icon: null,
    };
    return item;
}

/** Full upgrade blurb for info panel */
export function getItemInfo(item) {
    if (!item) return { title: '', body: '', stats: [] };
    const lines = [];
    const st = item.stats || {};
    if (st.damage) lines.push({ k: 'Damage', v: `+${st.damage}` });
    if (st.fireRate) lines.push({ k: 'Fire rate', v: `+${Math.round(st.fireRate * 100)}%` });
    if (st.range) lines.push({ k: 'Range', v: `+${st.range}` });
    if (st.maxHp) lines.push({ k: 'Max HP', v: `+${st.maxHp}` });
    if (st.speed) lines.push({ k: 'Speed', v: `+${st.speed}` });
    if (st.magnet) lines.push({ k: 'Magnet', v: `+${st.magnet}` });
    if (st.shield) lines.push({ k: 'Shield', v: `+${st.shield}` });
    if (st.armor) lines.push({ k: 'Armor', v: `+${st.armor}` });
    if (st.cargo) lines.push({ k: 'Cargo', v: `+${st.cargo}` });
    if (st.fuel) lines.push({ k: 'Fuel', v: `+${st.fuel}` });
    if (item.mutations) lines.push({ k: 'Mutations', v: `×${item.mutations}` });
    if (item.upgrades) lines.push({ k: 'Upgrades', v: `+${item.upgrades}` });

    const dur = item.maxDurability
        ? Math.round((item.durability ?? 100) / item.maxDurability * 100)
        : 100;

    return {
        title: item.name,
        rarity: item.rarity,
        slot: item.artefact ? 'artefact' : (item.slot || item.kind),
        body: item.desc || '',
        stats: lines,
        price: item.price,
        sell: getSellPrice(item),
        durability: dur,
        broken: !!item.broken,
        repairCost: getRepairCost(item),
        color: item.color,
        artefact: !!item.artefact,
        mutateCost: getMutateCost(item),
        upgradeCost: getUpgradeCost(item),
    };
}

export function getRepairCost(item) {
    if (!item) return 0;
    const missing = Math.max(0, (item.maxDurability || 100) - (item.durability ?? 100));
    if (missing <= 0 && !item.broken) return 0;
    const base = Number(item.price) || 20;
    const frac = item.broken ? 1 : missing / (item.maxDurability || 100);
    return Math.max(3, Math.round(base * 0.35 * Math.max(0.25, frac)));
}

export function repairItem(item) {
    if (!item) return false;
    item.durability = item.maxDurability || 100;
    item.broken = false;
    return true;
}

/** Damage a module; may break it */
export function damageItem(item, amount = 15) {
    if (!item || item.kind !== 'module') return false;
    item.maxDurability = item.maxDurability || 100;
    item.durability = Math.max(0, (item.durability ?? 100) - amount);
    if (item.durability <= 0) {
        item.broken = true;
        item.durability = 0;
        return true;
    }
    return false;
}

/** Safe sell price (never NaN) */
export function getSellPrice(item) {
    if (!item) return 1;
    const base = Number(item.price);
    const p = Number.isFinite(base) && base > 0 ? base : 10;
    return Math.max(1, Math.floor(p * 0.55));
}

function baseStatsForSlot(slot, mult) {
    const m = mult;
    switch (slot) {
        case 'weapon':
        case 'weapon2':
            return {
                damage: +(4 * m).toFixed(1),
                fireRate: +(0.08 * m).toFixed(2),
                range: Math.round(20 * m),
            };
        case 'shield':
            return { maxHp: Math.round(15 * m), shield: Math.round(10 * m) };
        case 'engine':
            return { speed: +(0.25 * m).toFixed(2), magnet: Math.round(12 * m) };
        case 'reactor':
            return { damage: +(2 * m).toFixed(1), fireRate: +(0.05 * m).toFixed(2), maxHp: Math.round(8 * m) };
        case 'hull':
            return { maxHp: Math.round(25 * m), armor: Math.round(5 * m) };
        case 'sensor':
            return { range: Math.round(40 * m), magnet: Math.round(20 * m) };
        case 'cargo':
            return { cargo: Math.round(2 * m), magnet: Math.round(8 * m) };
        case 'special':
            return { damage: +(3 * m).toFixed(1), speed: +(0.1 * m).toFixed(2), maxHp: Math.round(10 * m) };
        case 'wing':
            return { speed: +(0.15 * m).toFixed(2), armor: Math.round(3 * m) };
        default:
            return { maxHp: Math.round(10 * m) };
    }
}

function describeModule(slot, stats) {
    const parts = [];
    if (stats.damage) parts.push(`+${stats.damage} dmg`);
    if (stats.fireRate) parts.push(`+${Math.round(stats.fireRate * 100)}% firerate`);
    if (stats.range) parts.push(`+${stats.range} range`);
    if (stats.maxHp) parts.push(`+${stats.maxHp} HP`);
    if (stats.speed) parts.push(`+${stats.speed} speed`);
    if (stats.magnet) parts.push(`+${stats.magnet} magnet`);
    if (stats.shield) parts.push(`+${stats.shield} shield`);
    if (stats.armor) parts.push(`+${stats.armor} armor`);
    if (stats.cargo) parts.push(`+${stats.cargo} cargo`);
    return parts.join(' · ') || 'Ship module';
}

/** Trade goods / consumables. First arg may be a shape ('fuel') or a rarity. */
export function generateTradeGood(rarity = null, shape = null) {
    if (TRADE_SHAPES.includes(rarity) && !shape) {
        shape = rarity;
        rarity = null;
    }
    const useShape = TRADE_SHAPES.includes(shape) ? shape : pick(TRADE_SHAPES);
    const rar = (rarity && RARITY_MULT[rarity]) ? rarity : rarityRoll(-0.1);
    const color = pick(COLORS);
    const seed = Math.floor(Math.random() * 10000);
    const name = pick(TRADE_NAMES[useShape]);
    const mult = RARITY_MULT[rar] || 1;
    const price = Math.max(3, Math.round((8 + Math.random() * 25) * mult));

    const effects = {};
    if (useShape === 'fuel') effects.fuel = Math.round(15 * mult);
    if (useShape === 'crystal') effects.sellBonus = +(0.05 * mult).toFixed(2);
    if (useShape === 'chip') effects.range = Math.round(15 * mult);
    if (useShape === 'drone') effects.damage = +(1.5 * mult).toFixed(1);
    if (useShape === 'armor') effects.maxHp = Math.round(12 * mult);
    if (useShape === 'scrap') effects.scrap = 1;

    const item = {
        id: uid(),
        kind: 'good',
        slot: null,
        name,
        rarity: rar,
        color,
        seed,
        shape: useShape,
        stats: effects,
        price,
        desc: tradeDesc(useShape, effects),
        stackable: useShape === 'scrap' || useShape === 'crystal' || useShape === 'fuel',
        qty: 1,
        icon: null,
    };
    return item;
}

function tradeDesc(shape, e) {
    if (shape === 'fuel') return `Hyperjump fuel +${e.fuel}`;
    if (shape === 'crystal') return `Trade good · sell value ×${1 + (e.sellBonus || 0)}`;
    if (shape === 'chip') return `Nav upgrade · +${e.range} sensor range`;
    if (shape === 'drone') return `Combat aid · +${e.damage} damage`;
    if (shape === 'armor') return `Repair kit · +${e.maxHp} max HP when used`;
    if (shape === 'scrap') return 'Salvage material for trading';
    return 'Space commodity';
}

/** Random loot drop for kills / stations */
export function generateLoot(chanceModule = 0.35) {
    if (Math.random() < chanceModule) return generateModule();
    return generateTradeGood();
}

// ── Artefacts (combat relics that can mutate / upgrade) ──────

const ARTEFACT_NAMES = [
    'Rift Core', 'Void Shard', 'Plasma Seed', 'Echo Prism', 'Nova Heart',
    'Aether Lens', 'Warp Fang', 'Singularity Chip', 'Ghost Circuit', 'Star Bone',
    'Flux Idol', 'Chrono Spark', 'Ion Totem', 'Nebula Tear', 'Pulse Relic',
];

const MUTATION_TAGS = [
    'Unstable', 'Cracked', 'Blooming', 'Hollow', 'Radiant',
    'Twisted', 'Awakened', 'Feral', 'Sacred', 'Cursed',
];

/**
 * Combat artefact — equippable in special slot or kept for trade/mutate.
 */
export function generateArtefact(rarity = null, luck = 0) {
    const rar = rarity || rarityRoll(luck);
    const mult = RARITY_MULT[rar] || 1;
    const color = pick(COLORS);
    const seed = Math.floor(Math.random() * 10000);
    const baseName = pick(ARTEFACT_NAMES);
    const stats = {
        damage: +(2.5 * mult + Math.random() * mult).toFixed(1),
        maxHp: Math.round(8 * mult + Math.random() * 6 * mult),
        speed: +(0.08 * mult + Math.random() * 0.06 * mult).toFixed(2),
        fireRate: +(0.04 * mult).toFixed(2),
        range: Math.round(12 * mult),
    };
    // Focus one stat randomly
    const focus = pick(['damage', 'maxHp', 'speed', 'fireRate', 'range', 'armor', 'magnet']);
    if (focus === 'armor') stats.armor = Math.round(4 * mult);
    if (focus === 'magnet') stats.magnet = Math.round(18 * mult);
    if (stats[focus] != null && focus !== 'armor' && focus !== 'magnet') {
        stats[focus] = typeof stats[focus] === 'number' && stats[focus] < 3
            ? +(stats[focus] * 1.4).toFixed(2)
            : Math.round(stats[focus] * 1.35);
    }

    const raw = Object.values(stats).reduce((a, b) => a + Math.abs(Number(b) || 0), 0);
    const price = Math.max(18, Math.round((28 + raw * 4) * mult));

    const item = {
        id: uid(),
        kind: 'module', // equippable
        slot: 'special',
        name: baseName,
        rarity: rar,
        color,
        seed,
        shape: 'special',
        stats,
        price,
        desc: `Artefact · ${describeModule('special', stats)}`,
        durability: 100,
        maxDurability: 100,
        broken: false,
        artefact: true,
        mutations: 0,
        upgrades: 0,
        icon: null,
    };
    return item;
}

/** Cost in crystals to mutate an item (randomizes / twists stats). */
export function getMutateCost(item) {
    if (!item) return 0;
    const base = Number(item.price) || 20;
    const m = item.mutations || 0;
    return Math.max(8, Math.round(base * 0.25 + m * 12));
}

/** Cost to upgrade (stat boost + possible rarity step). */
export function getUpgradeCost(item) {
    if (!item) return 0;
    const base = Number(item.price) || 20;
    const u = item.upgrades || 0;
    const rarMul = RARITY_MULT[item.rarity] || 1;
    return Math.max(12, Math.round(base * 0.4 * rarMul + u * 18));
}

/**
 * Mutate — re-roll stat distribution, tint color, tag name.
 * @returns {{ ok: boolean, item?: object, reason?: string }}
 */
export function mutateItem(item) {
    if (!item || item.kind !== 'module' || item.broken) {
        return { ok: false, reason: 'Need a working module or artefact' };
    }
    const mult = RARITY_MULT[item.rarity] || 1;
    const slot = item.slot || 'special';
    // Blend base slot stats with chaos
    const base = baseStatsForSlot(slot, mult);
    const stats = { ...base };
    for (const k of Object.keys(stats)) {
        const v = Number(stats[k]) || 0;
        const twist = 0.7 + Math.random() * 0.8; // 0.7–1.5×
        stats[k] = v < 2 ? +(v * twist).toFixed(2) : Math.round(v * twist);
    }
    // Chance to gain a bonus stat
    if (Math.random() < 0.45) {
        const bonus = pick(['damage', 'speed', 'maxHp', 'armor', 'magnet', 'range', 'fireRate']);
        if (bonus === 'damage' || bonus === 'speed' || bonus === 'fireRate') {
            stats[bonus] = +((stats[bonus] || 0) + 0.5 * mult + Math.random() * mult).toFixed(2);
        } else {
            stats[bonus] = Math.round((stats[bonus] || 0) + 4 * mult + Math.random() * 6);
        }
    }
    item.stats = stats;
    item.mutations = (item.mutations || 0) + 1;
    item.color = pick(COLORS);
    item.seed = Math.floor(Math.random() * 10000);
    const tag = pick(MUTATION_TAGS);
    // Keep base name, strip previous mutation tags
    const root = (item.name || 'Relic').replace(/^(Unstable|Cracked|Blooming|Hollow|Radiant|Twisted|Awakened|Feral|Sacred|Cursed)\s+/i, '');
    item.name = `${tag} ${root}`;
    item.desc = `Mutated ×${item.mutations} · ${describeModule(slot, stats)}`;
    item.artefact = item.artefact || item.slot === 'special';
    item.price = Math.max(item.price || 20, Math.round((item.price || 20) * 1.08));
    return { ok: true, item };
}

/**
 * Upgrade — boost all stats, maybe raise rarity.
 * @returns {{ ok: boolean, item?: object, reason?: string, rarityUp?: boolean }}
 */
export function upgradeItem(item) {
    if (!item || item.kind !== 'module' || item.broken) {
        return { ok: false, reason: 'Need a working module or artefact' };
    }
    if ((item.upgrades || 0) >= 5) {
        return { ok: false, reason: 'Max upgrade rank (5)' };
    }
    const stats = { ...(item.stats || {}) };
    for (const k of Object.keys(stats)) {
        const v = Number(stats[k]) || 0;
        if (v === 0) continue;
        if (v < 3) stats[k] = +(v * 1.18 + 0.05).toFixed(2);
        else stats[k] = Math.round(v * 1.15 + 1);
    }
    item.stats = stats;
    item.upgrades = (item.upgrades || 0) + 1;
    let rarityUp = false;
    const order = RARITIES;
    const idx = order.indexOf(item.rarity);
    if (idx >= 0 && idx < order.length - 1 && Math.random() < 0.22 + item.upgrades * 0.04) {
        item.rarity = order[idx + 1];
        rarityUp = true;
    }
    item.price = Math.round((Number(item.price) || 20) * 1.22);
    item.desc = `Upgrade +${item.upgrades}${rarityUp ? ' · rarity up!' : ''} · ${describeModule(item.slot || 'special', stats)}`;
    return { ok: true, item, rarityUp };
}

/** Combat drop: module, good, or artefact */
export function generateCombatDrop(isBoss = false) {
    const r = Math.random();
    if (isBoss) {
        if (r < 0.4) return generateArtefact(rarityRoll(0.15), 0.2);
        if (r < 0.75) return generateModule(null, rarityRoll(0.1));
        return generateTradeGood(rarityRoll(0.05));
    }
    if (r < 0.08) return generateArtefact(null, 0);
    if (r < 0.22) return generateModule();
    if (r < 0.4) return generateTradeGood();
    return null;
}

/** Station stock: mix of modules + goods */
export function generateStationStock(count = 12, luck = 0) {
    const stock = [];
    for (let i = 0; i < count; i++) {
        if (Math.random() < 0.55) stock.push(generateModule(null, rarityRoll(luck)));
        else stock.push(generateTradeGood(rarityRoll(luck)));
    }
    return stock;
}

/**
 * Battlefield pad inventory. Fuel pads sell juice, armories sell guns,
 * markets mix goods + modules.
 * @param {'fuel'|'armory'|'market'} kind
 */
export function generateShopStock(kind = 'market', luck = 0) {
    const stock = [];
    if (kind === 'fuel') {
        for (let i = 0; i < 5; i++) stock.push(generateTradeGood(rarityRoll(luck), 'fuel'));
        stock.push(generateTradeGood(rarityRoll(luck), 'scrap'));
        if (Math.random() < 0.45) stock.push(generateModule('engine', rarityRoll(luck)));
        if (Math.random() < 0.35) stock.push(generateModule('reactor', rarityRoll(luck)));
        return stock;
    }
    if (kind === 'armory') {
        const slots = ['weapon', 'weapon2', 'hull', 'shield', 'special', 'wing'];
        for (let i = 0; i < 7; i++) {
            stock.push(generateModule(slots[i % slots.length], rarityRoll(luck)));
        }
        if (Math.random() < 0.4) stock.push(generateArtefact(rarityRoll(luck + 0.05), 0.1));
        stock.push(generateTradeGood(rarityRoll(luck), 'armor'));
        stock.push(generateTradeGood(rarityRoll(luck), 'drone'));
        return stock;
    }
    // market
    for (let i = 0; i < 8; i++) {
        if (Math.random() < 0.5) stock.push(generateModule(null, rarityRoll(luck)));
        else stock.push(generateTradeGood(rarityRoll(luck)));
    }
    stock.push(generateTradeGood(rarityRoll(luck), 'fuel'));
    stock.push(generateTradeGood(rarityRoll(luck), 'crystal'));
    return stock;
}

/** Starter loadout for new game */
export function starterLoadout() {
    return [
        generateModule('weapon', 'common'),
        generateModule('engine', 'common'),
        generateModule('hull', 'common'),
        generateTradeGood('fuel'),
        generateTradeGood('fuel'),
    ];
}
